// frontend/src/features/livestock/useOpenInTank.ts
import { useCallback, useEffect, useRef } from 'react';
import { useNavigate, type NavigateOptions } from 'react-router-dom';
import { useTank } from '@/contexts/TankContext';

/**
 * Opens a route that shows one tank's photos (the inbox, a photo in it). Those
 * pages show the selected tank, so when another tank is selected (the user
 * switched while the photos uploaded) the given tank is selected first.
 * The returned function always compares with the tank selected when it is called.
 * Navigation options (router state, replace) go along as given.
 */
export function useOpenInTank(): (
  tankId: string,
  path: string,
  options?: NavigateOptions
) => Promise<void> {
  const navigate = useNavigate();
  const { selectedTank, selectTank } = useTank();
  const latest = useRef({ selectedId: selectedTank?.id ?? null, selectTank, navigate });
  useEffect(() => {
    latest.current = { selectedId: selectedTank?.id ?? null, selectTank, navigate };
  });

  return useCallback(
    async (tankId: string, path: string, options?: NavigateOptions): Promise<void> => {
      if (latest.current.selectedId !== tankId) await latest.current.selectTank(tankId);
      if (options) latest.current.navigate(path, options);
      else latest.current.navigate(path);
    },
    []
  );
}
